import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export interface FilterOption {
	label: string
	value: string
}

export interface FilterDefinition {
	key: string
	label: string
	type: 'text' | 'select' | 'date'
	options?: FilterOption[]
}

interface FilterBarProps {
	filters: FilterDefinition[]
	values: Record<string, string>
	onChange: (values: Record<string, string>) => void
	onApply: () => void
	onClear: () => void
}

export function FilterBar({ filters, values, onChange, onApply, onClear }: FilterBarProps) {
	const updateFilter = (key: string, value: string) => {
		onChange({ ...values, [key]: value })
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') onApply()
	}

	const hasActiveFilters = Object.values(values).some((v) => v && v.trim() !== '')

	return (
		<div className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
			{filters.map((filter) => (
				<div key={filter.key} className="flex flex-col gap-1">
					<label className="text-xs font-medium text-gray-500">{filter.label}</label>
					{filter.type === 'text' && (
						<Input
							placeholder={filter.label}
							value={values[filter.key] ?? ''}
							onChange={(e) => updateFilter(filter.key, e.target.value)}
							onKeyDown={handleKeyDown}
							className="h-8 w-40 text-sm"
						/>
					)}
					{filter.type === 'select' && (
						<select
							value={values[filter.key] ?? ''}
							onChange={(e) => updateFilter(filter.key, e.target.value)}
							className="h-8 w-40 text-sm border border-gray-300 rounded-md px-2 bg-white"
						>
							<option value="">All</option>
							{filter.options?.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
					)}
					{filter.type === 'date' && (
						<input
							type="date"
							value={values[filter.key] ?? ''}
							onChange={(e) => updateFilter(filter.key, e.target.value)}
							className="h-8 w-40 text-sm border border-gray-300 rounded-md px-2 bg-white"
						/>
					)}
				</div>
			))}
			<div className="flex gap-2">
				<Button size="sm" onClick={onApply}>
					Filter
				</Button>
				{hasActiveFilters && (
					<Button size="sm" variant="outline" onClick={onClear}>
						Clear
					</Button>
				)}
			</div>
		</div>
	)
}
